import { useCallback, useMemo } from 'react';
import { findIndex, isEmpty } from 'lodash';
import { FORM_MODES } from 'config/formModes';
import { useCurrentValueGetter } from 'hooks';
import { useFormDataContext, useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { croppingTransformer } from '../utils/cropDataUtils';

export const useCropImageModalFormFields = (src, setSrc) => {
    const { mode: formMode } = useFormDataContext();
    const { value: thumbnails } = useFormFieldValue({ field: 'thumbnails' });
    // to edit/duplicate mode
    const { value: editCreativeCrop, onChange: onSubmitEditCreativeCrop } = useFormFieldValue({
        field: 'creativeCrop',
    });
    const thumbnailIndex = useMemo(() => findIndex(thumbnails, { displayUrl: src }), [thumbnails, src]);
    // to create mode
    const { value: { creativeCrop } = {}, onChange: onChangeThumbnailByIndex } = useFormFieldValue({
        field: `thumbnails.${thumbnailIndex}`,
    });
    const { onChange: onChangeThumbnailUrl } = useFormFieldValue({ field: `thumbnailUrl` });
    const getOnChangeThumbnailByIndex = useCurrentValueGetter(onChangeThumbnailByIndex);
    const getOnChangeThumbnailUrl = useCurrentValueGetter(onChangeThumbnailUrl);
    const initialCropData = formMode === FORM_MODES.CREATE ? creativeCrop : editCreativeCrop;

    const setCropData = useCallback(
        cropData => {
            if (!cropData) {
                return;
            }

            const finalCropData = croppingTransformer(cropData);
            const isCropped = !isEmpty(finalCropData);

            // if this is the edit screen, update the creativeCrop field
            if (editCreativeCrop) {
                onSubmitEditCreativeCrop({ cropData: finalCropData });
                return;
            }

            // otherwise, add the creative crop field to the submit form
            const onChangeThumbnail = getOnChangeThumbnailByIndex();
            onChangeThumbnail(prev => ({
                ...prev,
                cropped: isCropped,
                creativeCrop: {
                    cropData: isCropped ? finalCropData : [],
                },
            }));
        },
        [editCreativeCrop, getOnChangeThumbnailByIndex, onSubmitEditCreativeCrop]
    );

    const setThumbnailSrc = useCallback(
        src => {
            if (editCreativeCrop) {
                const onChangeThumbnailUrl = getOnChangeThumbnailUrl();
                onChangeThumbnailUrl(src);
                return;
            }

            const onChangeThumbnail = getOnChangeThumbnailByIndex();

            onChangeThumbnail(prev => ({
                ...prev,
                displayUrl: src,
                id: src,
                value: src,
            }));

            setSrc(src);
        },
        [editCreativeCrop, getOnChangeThumbnailByIndex, getOnChangeThumbnailUrl, setSrc]
    );

    return { initialCropData, setCropData, setThumbnailSrc };
};
